import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SupportIssue, SupportIssueDefect, SupportIssueFilter, SupportIssueNote, SupportIssueStatus, SupportIssueType } from '../models/SupportIssue';
import { AuthService } from 'src/app/services/auth.service';
import { tap } from 'rxjs/internal/operators/tap';

@Injectable({
  providedIn: 'root'
})
export class SupportIssueService {
  public supportIssues: SupportIssue[] = [];
  public loading: boolean = false;
  public issueTypes : SupportIssueType[];
  
  public defects: SupportIssueDefect[] = [];
  public issueTypesLoaded : Subject<SupportIssueType[]> = new Subject<SupportIssueType[]>();
  _issueCount : number = 0;
  public get issueSeverities(): { id: number, name: string }[] {
    return [
      { id: 10, name: 'Low' },
      { id: 20, name: 'Medium' },
      { id: 30, name: 'High' },
      { id: 40, name: 'Critical' }
    ];
  }
  public get issueStatuses(): { statusId: number, statusName: string }[] 
  {

    return [
      { statusId: 0, statusName: '' },
      { statusId: SupportIssueStatus.Open, statusName: 'Open' },
      { statusId: SupportIssueStatus.Verified, statusName: 'Verified' },
      { statusId: SupportIssueStatus.NotVerified, statusName: 'Not Verified' },
      { statusId: SupportIssueStatus.Working, statusName: 'Working' },
      { statusId: SupportIssueStatus.PendingManagerReview, statusName: 'Pending Manager Review' },
      { statusId: SupportIssueStatus.Resolved, statusName: 'Resolved' },
      { statusId: SupportIssueStatus.Cancelled, statusName: 'Cancelled' }
    ];

   }

  public get issueStatusesNonManager(): { statusId: number, statusName: string }[] {
    return [
      { statusId: SupportIssueStatus.Open, statusName: 'Open' },
      { statusId: SupportIssueStatus.Working, statusName: 'Working' },
      { statusId: SupportIssueStatus.PendingManagerReview, statusName: 'Pending Manager Review' },
    ];
  }


  constructor(public http: HttpClient, public toast: ToastrService, public auth : AuthService) { }
  
  saveIssue(issue: SupportIssue): Observable<any> {
    return this.http.post(environment.baseURl + '/api/issues', issue);
  }

  public get openIssueCount(): number {
    if(this.supportIssues.length == 0) return this._issueCount;
    else return (this.supportIssues.filter(rec => { return rec.status === 10 })).length;
  }

  fetchSupportIssues(hideErrors: boolean = false): void {
    this.loading = true;
    this.http.get(environment.baseURl + '/api/issues').subscribe((res: SupportIssue[]) => {
      if(res) {
        this.supportIssues = res;
        this.supportIssues.forEach(issue =>{
           let type = this.issueTypes.find(it => it.id == issue.supportIssueTypeId);
           if(type){
             issue.issueType = type;
           } 
        });
      }
      console.log('ISSUES: ', this.supportIssues);
      this.loading = false;
    },
    err => { if(!hideErrors) this.toast.error('Error fetching support issues.', '', { closeButton: true, disableTimeOut: true }); this.loading = false },
    () => { this.loading = false;})
  };

  fetchFilteredSupportIssues(filter: SupportIssueFilter, scopeToUser: boolean) {
    this.loading = true;
    if(scopeToUser) {
      filter.assignedTo = this.auth.loggedInUser.id;
    }
    this.http.post(environment.baseURl + '/api/issues/filtered', filter).subscribe((res: SupportIssue[]) => {
      if(res) {
        this.supportIssues = res;
        this.supportIssues.forEach(issue =>{
           let type = this.issueTypes.find(it => it.id == issue.supportIssueTypeId);
           if(type){
             issue.issueType = type;
           } 
        });
      }
      console.log('ISSUES: ', this.supportIssues);
      this.loading = false;
    },
    err => { this.toast.error('Error fetching support issues.', '', { closeButton: true, disableTimeOut: true }); this.loading = false },
    () => { this.loading = false;})
  }

  fetchDefectTypes(issueId: number) {
    return this.http.get<SupportIssueDefect[]>(environment.baseURl + '/api/issues/defect-types/' + issueId);
  }

  resolveIssue(issue: SupportIssue): void {
    issue.resolvedDate = new Date();
    //NOTE: The userId of the resolving user gets set at the api
    // issue.resolvingUserId = this.auth.loggedInUser.id;
    issue.status = SupportIssueStatus.Resolved;
    this.updateSupportIssue(issue).subscribe(res => {

    },
    err => {
      this.toast.error('Error resolving issue.', '', { closeButton: true, disableTimeOut: true })
    },
    () => this.toast.success('Issue resolved!', ''))
  };

  fetchSupportIssueTypes(hideErrors: boolean = false): void {
    this.loading = true;
    this.http.get(environment.baseURl + '/api/issues/issue-types').subscribe((res: SupportIssueType[]) => {
      this.issueTypes = res;
      this.issueTypesLoaded.next(this.issueTypes);
    },
    err => { if(this.auth.isAuthenticated) this.toast.error('Error fetching support issues.', '', { closeButton: true, disableTimeOut: true }); },
    () => { this.loading = false;})
  }

  fetchSupportIssueCounts() {
    this.http.get(environment.baseURl + '/api/issues/issue-counts').subscribe((res: number) => {
      this._issueCount = res;
    },
    err => { })
  }
  saveNote(note: SupportIssueNote): Observable<any> {
    return this.http.post<SupportIssueNote>(environment.baseURl + '/api/issues/note/add', note);
  } 

  updateSupportIssue(issueToUpdate: SupportIssue): Observable<any> {
    return this.http.post<SupportIssue>(environment.baseURl + '/api/issues/update', issueToUpdate);
  }

  fetchNotesForIssue(issue: SupportIssue) {
    return this.http.get(environment.baseURl + '/api/issues/note/' + issue.id);
  }

  fetchDefects(issueType: number) {
    this.defects = [];
    this.fetchDefectTypes(issueType).pipe(tap((_: SupportIssueDefect[]) => {
      this.defects = _;
    },
    err => {

    },
    () => {

    })).subscribe();
  }

}
